<template>
  <v-container>
    <div class="breadcrumbs-wrap">
      <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
    </div>
    <h1 class="title-pg">{{ $t('Карта') }}</h1>
    <div class="my-organization">
      <div class="map account-card-map-pg">
        <div class="map-body">
          <v-row>
            <v-col cols="12" md="12" class="map-aside">
              <div ref="map" style="width: 100%; height: 400px">
              </div>
              <div ref="popup" class="ol-popup" style="display: none">
                <a ref="popupCloser" class="ol-popup-closer" @click="closePopup"></a>
                <div contenteditable="true" ref="popupContent"></div>
              </div>
              <div class="map-panel">
                <p class="panel-tabs">
                  <a @click="showMapPanelTab('layers')" :class="mapPanelTabClasses('layers')">{{ $t('Слои') }}</a>
                  <a @click="showMapPanelTab('years')" :class="mapPanelTabClasses('years')">{{ $t('Года') }}</a>
                  <a @click="showMapPanelTab('faunas')" :class="mapPanelTabClasses('faunas')">{{ $t('Виды животных') }}</a>
                </p>
                <div class="panel-block layers-panel" v-show="mapPanel.tab === 'layers'">
                  <div class="buttons has-addons">
                    <button class="button is-light" v-for="(layer, key) in constBaseLayers"
                            :key="layer.id" :class="[{'is-info': layer.visible}, `layer-${layer.id}`]"
                            @click="showBaseLayer(key)">
                      {{ layer.title }}
                    </button>
                  </div>
                </div>
                <div class="panel-block years-panel" v-show="mapPanel.tab === 'years'">
                  <v-checkbox
                    :key="key"
                    v-for="(year, key) in faunaMapLayers.years"
                    :label="year.name"
                    v-model="year.visible"
                    @change="showFaunaLayer"
                  />
                </div>
                <div class="panel-block faunas-panel" v-show="mapPanel.tab === 'faunas'">
                  <div
                    v-for="(fauna, key) in faunaMapLayers.faunas"
                    :key="key"
                    class="panel-block__item"
                  >
                    <v-checkbox
                      :label="fauna.name"
                      v-model="fauna.visible"
                      @change="showFaunaLayer"
                    />
                    <span
                      class="rounded-mark"
                      :style="{ backgroundColor: fauna.iconColor }"
                    ></span>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import AnimalAccounting from '@/model/animalAccounting'

// OpenLayers map components
import { Feature, Map, View } from 'ol/index'
import Overlay from 'ol/Overlay'
import { OSM, BingMaps, Vector as VectorSource } from 'ol/source'
import { Group as LayerGroup, Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import { Point } from 'ol/geom'
import { Circle, Fill, Style } from 'ol/style'

// importing the OpenLayers stylesheet is required for having
import 'ol/ol.css'

export default {
  data: () => ({
    breadcrumbs: [],
    map: null,
    baseLayers: null,
    faunaLayers: null,
    faunaMapLayers: {},
    zoom: 6.5,
    center: [74.78142227997782, 41.50321074902047],
    rotation: 0,
    overlay: null,
    constBaseLayers: [
      {
        id: 'osm',
        title: 'OpenStreetMap',
        visible: true,
        layer: new TileLayer({
          visible: true,
          source: new OSM()
        })
      },
      {
        id: 'bingmaps-aerial',
        title: 'Bing (аэрофотоснимки)',
        visible: false,
        layer: new TileLayer({
          visible: false,
          source: new BingMaps({
            key: 'AnsqOI94mG7dyEHeNhgOS2CQuDo82Fl0ELasOPPHXzIakF5oQFMSny9qYkDQbTel',
            imagerySet: 'AerialWithLabelsOnDemand'
          })
        })
      },
      {
        id: 'bingmaps-canvas',
        title: 'Bing (облегченная версия)',
        visible: false,
        layer: new TileLayer({
          visible: false,
          source: new BingMaps({
            key: 'AnsqOI94mG7dyEHeNhgOS2CQuDo82Fl0ELasOPPHXzIakF5oQFMSny9qYkDQbTel',
            imagerySet: 'CanvasLight'
          })
        })
      },
      {
        id: 'bingmaps-road',
        title: 'Bing (дороги)',
        visible: false,
        layer: new TileLayer({
          visible: false,
          source: new BingMaps({
            key: 'AnsqOI94mG7dyEHeNhgOS2CQuDo82Fl0ELasOPPHXzIakF5oQFMSny9qYkDQbTel',
            imagerySet: 'RoadOnDemand'
          })
        })
      }
    ],
    mapPanel: {
      tab: 'layers'
    }
  }),
  created () {
    // загружаем данные, когда представление создано
    // и данные реактивно отслеживаются
    this.fetchData()
  },
  methods: {
    ...mapMutations(['setLoading']),
    fetchData () {
      this.setLoading(true)
      this.generateBreadcrumbs()
      AnimalAccounting.getAccountingFaunaCoordinatesData()
        .then((response) => {
          this.faunaMapLayers = response.data.faunaCoordinatesData
          this.initiateMap()
          this.setLoading(false)
        })
        .catch((error) => {
          console.log(error)
          this.$router.push({
            name: 'error',
            params: {
              status: error.response ? error.response.status : 500
            }
          })
          this.setLoading(false)
        })
    },
    // Initializing map
    initiateMap () {
      this.initiateBaseLayers()
      this.initiateFaunaLayers()
      const popup = this.$refs.popup
      const popupContent = this.$refs.popupContent
      const transContent = [this.$i18n.t('Год'), this.$i18n.t('Вид животного'), this.$i18n.t('Организация'), this.$i18n.t('Пользователь')]
      popup.style.display = 'block'
      this.overlay = new Overlay({
        element: popup,
        autoPan: true,
        autoPanAnimation: {
          duration: 250
        }
      })
      const overlay = this.overlay
      // this is where we create the OpenLayers map
      this.map = new Map({
        // the map will be created using the 'map' ref
        target: this.$refs.map,
        layers: [this.baseLayers, this.faunaLayers],
        // the map view will initially show the whole world
        view: new View({
          projection: 'EPSG:4326',
          zoom: this.zoom,
          center: this.center
        }),
        overlays: [this.overlay]
      })
      // popup overlay
      this.map.on('singleclick', function (evt) {
        const feature = this.forEachFeatureAtPixel(evt.pixel, function (feature, layer) {
          return {
            feature: feature,
            layer: layer
          }
        })
        if (feature) {
          // set table block for selected feature
          const content = '<table>\n' +
            '<tr>\n' +
            '<th>' + transContent[0] + '</th>\n' +
            '<td>\n' + feature.layer.get('yearName') + '</td>\n' +
            '</tr>\n' +
            '<tr>\n' +
            '<th>' + transContent[1] + '</th>\n' +
            '<td>\n' + feature.layer.get('faunaName') + '</td>\n' +
            '</tr>\n' +
            '<tr>\n' +
            '<th>' + transContent[2] + '</th>\n' +
            '<td>\n' + feature.layer.get('ooptName') + '</td>\n' +
            '</tr>\n' +
            '<tr>\n' +
            '<th>' + transContent[3] + '</th>\n' +
            '<td>\n' + feature.layer.get('userFio') + '</td>\n' +
            '</tr>\n' +
            '</table>'
          popupContent.innerHTML = content
          overlay.setPosition(evt.coordinate)
        }
      })
    },
    // Initializing baseLayers
    initiateBaseLayers () {
      const baseLayer = []
      this.constBaseLayers.forEach(function (element) {
        baseLayer.push(element.layer)
      })
      this.baseLayers = new LayerGroup({
        layers: baseLayer
      })
    },
    // Initializing ooptLayers
    initiateFaunaLayers () {
      // adding vectors for layers
      const faunaLayer = []
      Object.entries(this.faunaMapLayers.animalAccountings).forEach(([yearKey, year]) => {
        Object.entries(year.faunas).forEach(([faunaKey, fauna]) => {
          const features = []
          fauna.coordinates.forEach((value) => {
            const point = new Point([value[0], value[1]])
            features.push(new Feature(point))
          })
          const vectorSource = new VectorSource({
            features: features
          })
          const vectorLayer = new VectorLayer({
            id: year.yearId + ' ' + fauna.faunaId,
            yearId: year.yearId,
            faunaId: fauna.faunaId,
            source: vectorSource,
            style: new Style({
              image: new Circle({
                radius: 6,
                fill: new Fill({ color: fauna.iconColor })
              })
            }),
            visible: fauna.visible,
            layerName: year.yearName + ' ' + fauna.faunaName,
            yearName: year.yearName,
            faunaName: fauna.faunaName,
            ooptName: fauna.ooptName,
            userFio: fauna.userFio
          })
          faunaLayer.push(vectorLayer)
        })
      })
      // creating layer group
      this.faunaLayers = new LayerGroup({
        layers: faunaLayer
      })
    },
    generateBreadcrumbs () {
      this.breadcrumbs = [
        {
          text: this.$i18n.t('Главная'),
          disabled: false,
          href: '/'
        },
        {
          text: this.$i18n.t('Рабочий кабинет'),
          disabled: false,
          href: '/my-organization'
        },
        {
          text: this.$i18n.t('Карта'),
          disabled: true
        }
      ]
    },
    // show/hide base layers
    showBaseLayer (id) {
      this.constBaseLayers.forEach(function (element, index) {
        if (index === id) {
          element.visible = true
        } else {
          element.visible = false
        }
      })

      this.baseLayers.getLayers().forEach(function (element, index) {
        if (index === id) {
          element.setVisible(true)
        } else {
          element.setVisible(false)
        }
      })
    },
    // show/hide fauna layers
    showFaunaLayer () {
      // получаем список невыбранных животных
      const faunaIds = []
      Object.entries(this.faunaMapLayers.faunas).forEach(([faunaId, fauna]) => {
        if (!fauna.visible) {
          faunaIds.push(faunaId)
        }
      })
      // получаем список невыбранных годов
      const yearIds = []
      Object.entries(this.faunaMapLayers.years).forEach(([yearId, year]) => {
        if (!year.visible) {
          yearIds.push(yearId)
        }
      })

      this.faunaLayers.getLayers().forEach(function (element) {
        element.setVisible(true)
        const properties = element.getProperties()
        const existFauna = faunaIds.some(function (field) {
          return Number(field) === Number(properties.faunaId)
        })
        const existYear = yearIds.some(function (field) {
          return Number(field) === Number(properties.yearId)
        })
        if (existFauna || existYear) {
          element.setVisible(false)
        }
      })
    },
    // map panel
    mapPanelTabClasses (tab) {
      return {
        'is-active': this.mapPanel.tab === tab
      }
    },
    showMapPanelTab (tab) {
      if (this.mapPanel.tab === tab) {
        this.mapPanel.tab = ''
      } else {
        this.mapPanel.tab = tab
      }
    },
    // closing popup
    closePopup () {
      this.overlay.setPosition(undefined)
      this.$refs.popupCloser.blur()
    }
  }
}
</script>
